import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Form, Input } from 'antd';

import { parseNumericBigIntId } from './claims.utils';

export const Route = createFileRoute('/admin-console/advanced/claims')({
    component: ClaimUtilities,
    staticData: {
        breadcrumb: {
            title: 'Claims',
        },
        ui: {
            title: 'Utilities',
            widthVariant: 'compact',
            menuDescription: 'Claims utilities',
        },
    },
});

type ClaimUtilitiesFormValues = {
    orgId: string;
    claimId: string;
};

function ClaimUtilities() {
    const [form] = Form.useForm<ClaimUtilitiesFormValues>();
    const { trpc } = useTrpc();

    const { mutate: generatePdf, isPending: processing } = trpc.admin.claim.generatePdf.useMutation();

    const handleGeneratePdf = ({ orgId, claimId }: ClaimUtilitiesFormValues) => {
        message.info('Generating PDF...');

        generatePdf(
            {
                orgId: parseNumericBigIntId(orgId),
                claimId: parseNumericBigIntId(claimId),
            },
            {
                onSuccess: () => {
                    message.success('PDF generated successfully');
                },
                onError(error) {
                    errorMessage.show(error);
                },
            },
        );
    };
    return (
        <PageController route={Route}>
            <Card title="Claim Utilities">
                <Form<ClaimUtilitiesFormValues> form={form} layout="vertical" onFinish={handleGeneratePdf}>
                    <Form.Item
                        label="Organisation ID"
                        name="orgId"
                        className="max-w-xs"
                        rules={[
                            { required: true, message: 'Please input the organisation ID' },
                            { pattern: /^\d+$/, message: 'Please input a valid number' },
                        ]}
                    >
                        <Input inputMode="numeric" placeholder="Enter numeric organisation ID" />
                    </Form.Item>
                    <Form.Item
                        label="Internal claim ID"
                        name="claimId"
                        className="max-w-xs"
                        extra="Use the numeric internal claim ID, not an external reference such as ET-001513."
                        rules={[
                            { required: true, message: 'Please input the internal claim ID' },
                            { pattern: /^\d+$/, message: 'Please input a valid number' },
                        ]}
                    >
                        <Input inputMode="numeric" placeholder="Enter numeric internal claim ID" />
                    </Form.Item>
                    <Form.Item>
                        <Button htmlType="submit" loading={processing} type="primary">
                            Generate Claim Summary PDF
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </PageController>
    );
}
